// CHAT-PRESENCE (founder's design discussion 2026-10-29): the ring on a DE's avatar and its hover
// card — one component wherever a DE appears (the chat sidebar's Team and DMs, the thread pane's
// agent chips, the chart's seat cards). The ring, in priority order (`deRingState`):
//   needs you  amber with "?"    an ask or approval waits on the viewer
//   stuck      red with "!"      the latest run failed and nobody acknowledged it
//   working    green, breathing  a run is live (static under prefers-reduced-motion)
//   waiting    grey, dashed      a run waits, not on the viewer
//   available  thin grey ring
//   off        no ring, 60 %     paused, onboarding or retired
// Never blue: blue is unread. The card says what it does: "Working on '<task>' · since <time> ·
// in #channel › topic", with Run, Screen and Thread links, the ask with Approve or Reply, or the
// failure's first line with Open run and Got it.
import type { OperationOutput } from "@companyos/sdk";
import {
  AvatarTile,
  type AvatarTileSize,
  Button,
  cn,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ActivityIcon,
  CheckIcon,
  MessageSquareIcon,
  MonitorIcon,
  SendIcon,
  ThumbsUpIcon,
} from "lucide-react";
import { createContext, type ReactElement, type ReactNode, useContext, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { helperCounts, helpersText, useHelpers } from "../agents/helpers";
import { deLabel } from "../de/de-name";
import { problemText } from "../ops-parts";
import { digitalEmployeesQuery } from "../queries";
import { useEveryPage } from "../use-every-page";

export type DePresence = OperationOutput<"dePresence.list">["data"][number];
export type DeRing = DePresence["state"];

/** The ring's word (the card's status, a row's accessible name). */
export const RING_WORD: Record<DeRing, string> = {
  needs_you: "Needs you",
  stuck: "Stuck",
  working: "Working",
  waiting: "Waiting on others",
  available: "Available",
  off: "Off",
};

export const presenceKeys = {
  all: (co: string) => ["company", co, "chat", "de-presence"] as const,
};

/** Every DE's ring for the viewer; refreshed every 15 s (and after any chat write that matters). */
export const dePresenceQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: presenceKeys.all(co),
    queryFn: () => api.client.dePresence.list({ params: { co } }),
    refetchInterval: 15_000,
    retry: false,
  });

/** The rings by DE membership id (empty while loading, or when Chat is off). */
export function useDePresence(
  api: Api,
  co: string,
  enabled = true,
): ReadonlyMap<string, DePresence> {
  const q = useQuery({ ...dePresenceQuery(api, co), enabled });
  const rows = q.data?.data ?? [];
  return new Map(rows.map((r) => [r.membership_id, r]));
}

/** The rings a screen fetched once, for parts deep in it (the chart's seat cards). */
const PresenceContext = createContext<ReadonlyMap<string, DePresence>>(new Map());
export const DePresenceProvider = PresenceContext.Provider;
export const usePresenceOf = (membershipId: string | undefined) => {
  const all = useContext(PresenceContext);
  return membershipId ? all.get(membershipId) : undefined;
};

/**
 * What a screen's DE avatars need to open the hover card (the Team list's): the chart's seat
 * cards read it with the rings. Null: rings only, no card.
 */
export type PresenceCardScope = Pick<CardTarget, "api" | "co" | "slug" | "canAct">;
const PresenceCardContext = createContext<PresenceCardScope | null>(null);
export const DePresenceCardProvider = PresenceCardContext.Provider;
export const usePresenceCardScope = () => useContext(PresenceCardContext);

const RING_EDGE: Record<DeRing, string> = {
  needs_you: "border-2 border-warning",
  stuck: "border-2 border-destructive",
  working:
    "border-2 border-success animate-[de-ring-breathe_2.8s_ease-in-out_infinite] motion-reduce:animate-none",
  waiting: "border-[1.5px] border-dashed border-muted-foreground/70",
  available: "border border-border-strong",
  off: "",
};

const INSET: Record<AvatarTileSize, string> = {
  xs: "-inset-[3px] rounded-[5px]",
  sm: "-inset-[3px] rounded-[7px]",
  md: "-inset-[3px] rounded-[7px]",
  lg: "-inset-[3px] rounded-[8px]",
};

/**
 * A DE's avatar with its ring. `state` undefined (not loaded yet) draws the plain tile, so a list
 * never flashes a wrong state.
 */
export function DeAvatar({
  name,
  state,
  hint,
  size = "xs",
  className,
}: {
  name: string;
  state: DeRing | undefined;
  /** MD-STATUS-FIX: what waits behind a "?" or "!" ("8 lessons to review"), as its tooltip. */
  hint?: string | null | undefined;
  size?: AvatarTileSize;
  className?: string;
}) {
  const marked = state === "needs_you" || state === "stuck";
  return (
    <span
      data-slot="de-avatar"
      data-de-ring={state}
      title={marked && state ? ringTitle(state, hint) : undefined}
      className={cn("relative inline-flex shrink-0", className)}
    >
      <AvatarTile
        name={workerName(name)}
        kind="agent"
        size={size}
        aria-hidden="true"
        className={state === "off" ? "opacity-60" : undefined}
      />
      {state && state !== "off" ? (
        <span
          aria-hidden="true"
          data-slot="de-ring"
          className={cn("pointer-events-none absolute", INSET[size], RING_EDGE[state])}
        />
      ) : null}
      {state === "needs_you" || state === "stuck" ? (
        <span
          aria-hidden="true"
          data-slot="de-ring-mark"
          className={cn(
            "absolute -right-1.5 -bottom-1.5 flex size-3 items-center justify-center rounded-full text-[8px] leading-none font-bold ring-2 ring-background",
            state === "needs_you"
              ? "bg-warning text-foreground"
              : "bg-destructive text-destructive-foreground",
          )}
        >
          {state === "needs_you" ? "?" : "!"}
        </span>
      ) : null}
    </span>
  );
}

/** MD-STATUS-FIX: "Needs you: 8 lessons to review" (the ring's word alone without a hint). */
export function ringTitle(state: DeRing, hint?: string | null): string {
  return hint ? `${RING_WORD[state]}: ${hint}` : RING_WORD[state];
}

/** What waits behind a DE's mark: its ask (needs you) or its failure's first line (stuck). */
export function ringHint(p: DePresence | undefined): string | null {
  if (!p) return null;
  if (p.state === "needs_you") return p.ask?.text ?? null;
  if (p.state === "stuck") return p.error ?? null;
  return null;
}

/** "12 min", "3 h", "2 d" since an ISO time. */
export function sinceText(iso: string, now = new Date()): string {
  const min = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} d`;
}

/** "in #recruiting › W43 shortlist", "in your DM". */
export function placeText(place: NonNullable<DePresence["place"]>): string {
  const where = place.is_dm ? "your DM" : `#${place.channel_name}`;
  return place.topic_name ? `in ${where} › ${place.topic_name}` : `in ${where}`;
}

/** What the card says the DE is doing, in one line. */
export function doingLine(p: DePresence, now = new Date()): string | null {
  const parts: string[] = [];
  if (p.state === "working")
    parts.push(p.task ? `Working on '${p.task.title}'` : "Working on a run");
  else if (p.state === "waiting")
    parts.push(p.task ? `Waiting on '${p.task.title}'` : "Waiting on someone else");
  else if (p.state === "needs_you" && p.task) parts.push(`On '${p.task.title}'`);
  else if (p.state === "stuck" && p.task) parts.push(`Stopped on '${p.task.title}'`);
  if (parts.length === 0 && !p.since) return null;
  if (p.since) parts.push(`since ${sinceText(p.since, now)}`);
  if (p.place) parts.push(placeText(p.place));
  return parts.join(" · ");
}

type CardTarget = {
  api: Api;
  co: string;
  slug: string;
  /** The DE (membership id and display name). */
  de: { id: string; name: string };
  presence: DePresence | undefined;
  /** The viewer may act (a member, not read-only): Approve, Reply, Got it. */
  canAct: boolean;
  /** HELPERS: its helpers ("14 helpers · 3 working"; nothing at zero or when not given). */
  helpers?: { total: number; working: number } | undefined;
};

/**
 * Hovering `children` (a row, a chip, an avatar) opens the DE's card. Without presence (still
 * loading, or Chat off) it is the children alone.
 */
export function DeHoverCard({
  children,
  render,
  ...t
}: CardTarget & { children: ReactNode; render?: ReactElement }) {
  if (!t.presence) return <>{children}</>;
  return (
    <HoverCard>
      <HoverCardTrigger
        delay={350}
        render={render ?? <span className="inline-flex min-w-0" />}
        data-slot="de-hover-trigger"
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        className="w-72 p-3"
        data-slot="de-card"
        // The card is portalled, but React bubbles its clicks to the trigger's parents: a click
        // in it must not count as one on the row or seat card it opened from.
        onClick={(e) => e.stopPropagation()}
      >
        <DeCardBody {...t} presence={t.presence} />
      </HoverCardContent>
    </HoverCard>
  );
}

function DeCardBody({
  api,
  co,
  slug,
  de,
  presence: p,
  canAct,
  helpers,
}: CardTarget & { presence: DePresence }) {
  // HELPERS-2 (Codex HELPERS): every hover card (chart seats, chat, threads) carries the helpers
  // line and the DE's person name, not only the Team list's: read here when not passed in (the
  // card mounts on hover only; both lists are cached and shared).
  const own = useHelpers(api, co, helpers === undefined);
  const counts = helpers ?? helperCounts(own.byLead.get(de.id), own.now);
  const des = useInfiniteQuery({ ...digitalEmployeesQuery(api, co), retry: false });
  // Every page (Codex HELPERS-2 #3): a DE past the first page keeps its person name.
  useEveryPage(des);
  const personName = des.data?.pages
    .flatMap((x) => x.data)
    .find((d) => d.membership_id === de.id)?.person_name;
  const name = deLabel(de.name, personName);
  const line = doingLine(p);
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: presenceKeys.all(co) });
  const ack = useMutation({
    mutationFn: () => api.client.dePresence.ack({ params: { co, id: de.id } }),
    onSuccess: refresh,
    onError: (e) =>
      toast.add({
        title: "Could not clear it",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  const placeLink = p.place ? (
    <Link
      to={p.place.topic_id ? "/c/$slug/chat/$channelId/$topicId" : "/c/$slug/chat/$channelId"}
      params={
        p.place.topic_id
          ? { slug, channelId: p.place.channel_id, topicId: p.place.topic_id }
          : ({ slug, channelId: p.place.channel_id } as never)
      }
      search={keepFocus as never}
      className="inline-flex items-center gap-1 text-xs text-primary-text hover:underline"
      data-slot="de-card-thread"
    >
      <MessageSquareIcon aria-hidden="true" className="size-3" />
      Thread
    </Link>
  ) : null;
  return (
    <div className="flex flex-col gap-2" data-de-card={de.id} data-de-ring={p.state}>
      <div className="flex items-center gap-2">
        <DeAvatar name={name} state={p.state} hint={ringHint(p)} size="sm" />
        <span className="min-w-0 truncate font-semibold">{name}</span>
        <span className="ml-auto shrink-0 text-xs text-muted-foreground" data-slot="de-card-state">
          {RING_WORD[p.state]}
        </span>
      </div>
      {line ? (
        <p className="text-xs text-muted-foreground" data-slot="de-card-doing">
          {line}
        </p>
      ) : null}
      {counts.total > 0 ? (
        <Link
          to="/c/$slug/agents/de/$id"
          params={{ slug, id: de.id }}
          search={{ ...(keepFocus({}) as object), tab: "helpers" } as never}
          className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
          data-slot="de-card-helpers"
        >
          {helpersText(counts.total, counts.working)}
        </Link>
      ) : null}
      {p.state === "needs_you" && p.ask ? (
        <AskBlock api={api} co={co} slug={slug} ask={p.ask} canAct={canAct} onDone={refresh} />
      ) : null}
      {p.state === "stuck" ? (
        <div className="flex flex-col gap-1.5" data-slot="de-card-error">
          {p.error ? <p className="text-xs text-destructive">{p.error}</p> : null}
          <div className="flex items-center gap-1.5">
            {p.run_id ? (
              <Button
                size="xs"
                variant="secondary"
                nativeButton={false}
                render={
                  <Link
                    to="/c/$slug/agents/runs/$id"
                    params={{ slug, id: p.run_id }}
                    search={keepFocus as never}
                  />
                }
              >
                Open run
              </Button>
            ) : null}
            {canAct ? (
              <Button
                size="xs"
                variant="ghost"
                disabled={ack.isPending}
                onClick={() => ack.mutate()}
                title="Clear the stuck mark for everyone"
              >
                <CheckIcon />
                Got it
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 border-t border-subtle pt-2">
        <Link
          to="/c/$slug/agents/activity"
          params={{ slug }}
          search={{ ...(keepFocus({}) as object), who: `de:${de.id}` } as never}
          className="inline-flex items-center gap-1 text-xs text-primary-text hover:underline"
          data-slot="de-card-run"
        >
          <ActivityIcon aria-hidden="true" className="size-3" />
          Run
        </Link>
        {p.screen_lease_id ? (
          <Link
            to="/c/$slug/live"
            params={{ slug }}
            search={{ lease: p.screen_lease_id } as never}
            className="inline-flex items-center gap-1 text-xs text-primary-text hover:underline"
            data-slot="de-card-screen"
          >
            <MonitorIcon aria-hidden="true" className="size-3" />
            Screen
          </Link>
        ) : null}
        {placeLink}
      </div>
    </div>
  );
}

/** "Needs you": the ask in one sentence, with Approve (an approval) or Reply (a thread). */
function AskBlock({
  api,
  co,
  slug,
  ask,
  canAct,
  onDone,
}: {
  api: Api;
  co: string;
  slug: string;
  ask: NonNullable<DePresence["ask"]>;
  canAct: boolean;
  onDone: () => void;
}) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const approve = useMutation({
    mutationFn: (id: string) => api.client.approvals.approve({ params: { co, id } }),
    onSuccess: () => {
      toast.add({ title: "Approved", type: "success" });
      onDone();
    },
    onError: (e) =>
      toast.add({
        title: "Could not approve",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  const reply = useMutation({
    mutationFn: (threadId: string) =>
      api.client.messages.create({
        params: { co, id: threadId },
        body: {
          client_id: crypto.randomUUID(),
          body_markdown: text.trim(),
          // In a reply thread the answer goes under its root, where the DE asked.
          ...(ask.parent_message_id ? { parent_message_id: ask.parent_message_id } : {}),
        },
      }),
    onSuccess: () => {
      toast.add({ title: "Reply sent", type: "success" });
      setReplying(false);
      setText("");
      onDone();
    },
    onError: (e) =>
      toast.add({ title: "Could not send", description: problemText(e, e.message), type: "error" }),
  });
  return (
    <div
      className="flex flex-col gap-1.5 rounded-md bg-warning-soft px-2 py-1.5"
      data-slot="de-card-ask"
    >
      <p className="text-xs text-foreground">{ask.text}</p>
      {canAct ? (
        <div className="flex items-center gap-1.5">
          {ask.kind === "approval" && !ask.approval_id ? (
            // MD-STATUS-FIX: lessons are reviewed one by one in the Memory lane
            <Button
              size="xs"
              nativeButton={false}
              render={
                <Link
                  to="/c/$slug/inbox"
                  params={{ slug }}
                  search={{ tab: "memory" } as never}
                  data-slot="de-card-review"
                />
              }
            >
              Review
            </Button>
          ) : null}
          {ask.kind === "approval" && ask.approval_id ? (
            <Button
              size="xs"
              disabled={approve.isPending}
              onClick={() => approve.mutate(ask.approval_id as string)}
            >
              <ThumbsUpIcon />
              Approve
            </Button>
          ) : null}
          {ask.kind === "input" && ask.thread_id && !replying ? (
            <Button size="xs" onClick={() => setReplying(true)}>
              <MessageSquareIcon />
              Reply
            </Button>
          ) : null}
          {ask.kind === "screen" && ask.screen_lease_id ? (
            <Button
              size="xs"
              nativeButton={false}
              render={
                <Link
                  to="/c/$slug/live"
                  params={{ slug }}
                  search={{ lease: ask.screen_lease_id } as never}
                />
              }
            >
              <MonitorIcon />
              Open screen
            </Button>
          ) : null}
        </div>
      ) : null}
      {replying && ask.thread_id ? (
        <form
          className="flex items-end gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) reply.mutate(ask.thread_id as string);
          }}
        >
          <Textarea
            aria-label="Reply"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-0 text-xs"
          />
          <Button
            type="submit"
            size="icon-xs"
            aria-label="Send reply"
            disabled={reply.isPending || !text.trim()}
          >
            <SendIcon />
          </Button>
        </form>
      ) : null}
    </div>
  );
}
